import { usePlatform } from '@/composables/usePlatform'
import { useBaseBusinessStore } from '@/store/base/baseStore'
export const COMMITMENT_SIGN_STATUS = {
  UNSIGNED: 0,
  SIGNED: 1
} as const

export const COMMITMENT_DEADLINE_TYPE = {
  REGISTER_SUCCESS: 1,
  EXAM_APPLIED: 2,
  ENROLLMENT_DAYS: 3
} as const

export const COMMITMENT_EXPIRED_MESSAGE =
  '您已错过了承诺书签署时间，无法签署承诺书'

export interface AssessmentStandardConfig {
  id?: string | number
  standardId?: string | number
  committedConfig?: string
  signStatus?: boolean | number | string
  signStauts?: boolean | number | string
  signDeadlineType?: number | string
  signDeadlineDays?: number | string
  signDeadlineTime?: string | null
  [key: string]: any
}

export interface CommitmentDetail {
  standardId?: string | number
  committedConfig?: string
  signStatus?: boolean | number | string
  signStauts?: boolean | number | string
  signDeadlineType?: number | string
  signDeadlineDays?: number | string
  signDeadlineTime?: string | null
  [key: string]: any
}

export type CommitmentGateAction =
  | 'none'
  | 'sign'
  | 'apply'
  | 'expired'
  | 'error'

export interface CommitmentGateState {
  config?: AssessmentStandardConfig | null
  detail?: CommitmentDetail | null
  standardId?: string | number
  scene?: 'home' | 'learn' | 'exam'
}

export interface CommitmentGateResult extends CommitmentGateState {
  pass: boolean
  action: CommitmentGateAction
  message: string
  applied?: boolean
}

const normalizeNumber = (value: unknown, fallback = 0) => {
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : fallback
}

const normalizeDeadlineTime = (value: unknown) => {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

const hasOwnSignDeadlineTime = (
  source?: AssessmentStandardConfig | CommitmentDetail | null
) => {
  return Boolean(
    source &&
      Object.prototype.hasOwnProperty.call(source, 'signDeadlineTime')
  )
}

const parseDate = (value?: string) => {
  const deadlineTime = normalizeDeadlineTime(value)
  if (!deadlineTime) return null
  const normalized = deadlineTime.includes('T')
    ? deadlineTime
    : deadlineTime.replace(/-/g, '/')
  const date = new Date(normalized)
  return Number.isNaN(date.getTime()) ? null : date
}

const hasExplicitTimePart = (value: string) => {
  return /(?:T|\s)\d{1,2}:\d{2}(?::\d{2})?/.test(value)
}

const formatDate = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function useCommitmentSigning() {
  const { isAdultEducation } = usePlatform()
  const educStore = useBaseBusinessStore('app-api/educ')
  const lrnStore = useBaseBusinessStore('app-api/lrn')
  const exmStore = useBaseBusinessStore('app-api/exm')

  const fetchAssessmentConfig = async (force = false) => {
    if (!force && !isAdultEducation.value) return null
    const res = await educStore.getRequest<AssessmentStandardConfig>(
      'app-assessment-standard/config'
    )
    return res?.data || null
  }

  const getStandardId = (config?: AssessmentStandardConfig | null) => {
    return config?.id ?? config?.standardId
  }

  const fetchCommitmentDetail = async (standardId?: string | number) => {
    if (!standardId) return null
    const res = await lrnStore.getRequest<CommitmentDetail>(
      'app-commitment-sign/detail',
      { standardId }
    )
    return res?.data || null
  }

  const fetchCommitmentState = async (force = false) => {
    const config = await fetchAssessmentConfig(force)
    const standardId = getStandardId(config)
    const detail = await fetchCommitmentDetail(standardId)
    return {
      config,
      detail,
      standardId
    }
  }

  const checkExamApplied = async () => {
    const res = await exmStore.getRequest<boolean>(
      'app-student-test-paper/is-applied'
    )
    return Boolean(res?.data)
  }

  const isCommitmentSigned = (detail?: CommitmentDetail | null) => {
    const status = detail?.signStauts ?? detail?.signStatus
    return (
      status === true ||
      status === 'true' ||
      normalizeNumber(status) === COMMITMENT_SIGN_STATUS.SIGNED
    )
  }

  const getSignDeadlineType = (
    config?: AssessmentStandardConfig | null,
    detail?: CommitmentDetail | null
  ) => {
    return normalizeNumber(config?.signDeadlineType ?? detail?.signDeadlineType)
  }

  const getSignDeadlineDays = (
    config?: AssessmentStandardConfig | null,
    detail?: CommitmentDetail | null
  ) => {
    return normalizeNumber(config?.signDeadlineDays ?? detail?.signDeadlineDays)
  }

  const getSignDeadlineTime = (
    config?: AssessmentStandardConfig | null,
    detail?: CommitmentDetail | null
  ) => {
    if (hasOwnSignDeadlineTime(config)) {
      return normalizeDeadlineTime(config?.signDeadlineTime)
    }
    return normalizeDeadlineTime(detail?.signDeadlineTime)
  }

  const getDeadlineDateText = (
    config?: AssessmentStandardConfig | null,
    detail?: CommitmentDetail | null
  ) => {
    const date = parseDate(getSignDeadlineTime(config, detail))
    return date ? formatDate(date) : ''
  }

  const getDeadlineLabel = (
    config?: AssessmentStandardConfig | null,
    detail?: CommitmentDetail | null
  ) => {
    const dateText = getDeadlineDateText(config, detail)
    if (dateText) return `截止日期${dateText}`

    const days = getSignDeadlineDays(config, detail)
    if (days) return `注册入学后 ${days} 天内`

    return '规定期限内'
  }

  const getType3PromptText = (
    config?: AssessmentStandardConfig | null,
    detail?: CommitmentDetail | null
  ) => {
    const dateText = getDeadlineDateText(config, detail)
    if (dateText) {
      return `根据要求，您需要在截止日期${dateText}之前签署学习承诺书`
    }

    const days = getSignDeadlineDays(config, detail)
    if (days) {
      return `根据要求，您需要在注册入学后 ${days} 天内签署学习承诺书`
    }

    return '根据要求，您需要在规定期限内签署学习承诺书'
  }

  const getCommitmentContent = (
    config?: AssessmentStandardConfig | null,
    detail?: CommitmentDetail | null
  ) => {
    return detail?.committedConfig || config?.committedConfig || ''
  }

  const isDeadlineExpired = (
    config?: AssessmentStandardConfig | null,
    detail?: CommitmentDetail | null,
    now = Date.now()
  ) => {
    const deadlineTime = getSignDeadlineTime(config, detail)
    const date = parseDate(deadlineTime)
    if (!date) return false

    if (!hasExplicitTimePart(deadlineTime)) {
      date.setHours(23, 59, 59, 999)
    }
    return now > date.getTime()
  }

  const isKnownDeadlineType = (deadlineType: number) => {
    return Object.values(COMMITMENT_DEADLINE_TYPE).includes(
      deadlineType as (typeof COMMITMENT_DEADLINE_TYPE)[keyof typeof COMMITMENT_DEADLINE_TYPE]
    )
  }

  const getSignPromptText = (
    config?: AssessmentStandardConfig | null,
    detail?: CommitmentDetail | null,
    scene: CommitmentGateState['scene'] = 'home'
  ) => {
    if (scene === 'learn') {
      return '根据要求，您需要签署承诺书之后才能学习。'
    }
    if (scene === 'exam') {
      return '根据要求，您需要签署承诺书之后才能考试。'
    }

    const deadlineType = getSignDeadlineType(config, detail)

    if (deadlineType === COMMITMENT_DEADLINE_TYPE.REGISTER_SUCCESS) {
      return '根据要求，您需要在注册成功之后签署学习承诺书'
    }
    if (deadlineType === COMMITMENT_DEADLINE_TYPE.EXAM_APPLIED) {
      return '根据要求，您已报名考试，请尽快签署承诺书，避免影响您的学习和考试。'
    }
    if (deadlineType === COMMITMENT_DEADLINE_TYPE.ENROLLMENT_DAYS) {
      return getType3PromptText(config, detail)
    }
    return '根据要求，您需要签署学习承诺书'
  }

  const shouldBlockLearningOrExam = (
    config?: AssessmentStandardConfig | null,
    detail?: CommitmentDetail | null
  ) => {
    const deadlineType = getSignDeadlineType(config, detail)
    return isKnownDeadlineType(deadlineType) && !isCommitmentSigned(detail)
  }

  const resolveCommitmentGate = async (
    state?: CommitmentGateState
  ): Promise<CommitmentGateResult> => {
    try {
      const currentState = state || (await fetchCommitmentState())
      const config = currentState.config || null
      const detail = currentState.detail || null
      const standardId = currentState.standardId || getStandardId(config)
      const scene = state?.scene || 'home'
      const baseResult = { config, detail, standardId }

      if (!standardId) {
        return {
          ...baseResult,
          pass: true,
          action: 'none',
          message: ''
        }
      }

      if (isDeadlineExpired(config, detail)) {
        return {
          ...baseResult,
          pass: false,
          action: 'expired',
          message: COMMITMENT_EXPIRED_MESSAGE
        }
      }

      const deadlineType = getSignDeadlineType(config, detail)
      if (!isKnownDeadlineType(deadlineType) || isCommitmentSigned(detail)) {
        return {
          ...baseResult,
          pass: true,
          action: 'none',
          message: ''
        }
      }

      if (deadlineType === COMMITMENT_DEADLINE_TYPE.EXAM_APPLIED) {
        const applied = await checkExamApplied()
        if (!applied) {
          return {
            ...baseResult,
            pass: false,
            action: 'apply',
            applied,
            message: '根据要求，您需要在报名考试之后才能签署承诺书'
          }
        }

        return {
          ...baseResult,
          pass: false,
          action: 'sign',
          applied,
          message: getSignPromptText(config, detail, scene)
        }
      }

      return {
        ...baseResult,
        pass: false,
        action: 'sign',
        message: getSignPromptText(config, detail, scene)
      }
    } catch (error) {
      console.error('承诺书状态判断失败:', error)
      return {
        config: state?.config || null,
        detail: state?.detail || null,
        standardId: state?.standardId,
        pass: false,
        action: 'error',
        message: '承诺书状态获取失败，请稍后重试'
      }
    }
  }

  return {
    fetchAssessmentConfig,
    fetchCommitmentDetail,
    fetchCommitmentState,
    checkExamApplied,
    getStandardId,
    isCommitmentSigned,
    getSignDeadlineType,
    getSignDeadlineDays,
    getSignDeadlineTime,
    getDeadlineDateText,
    getDeadlineLabel,
    getType3PromptText,
    getCommitmentContent,
    isDeadlineExpired,
    getSignPromptText,
    shouldBlockLearningOrExam,
    resolveCommitmentGate
  }
}
